import { Component, Element, Event, EventEmitter, h, Host, Listen, Prop, Watch } from "@stencil/core";

import { StzhRadioBlurEvent, StzhRadioChangeEvent, StzhRadioFocusEvent } from "../../index";

import { hasSlot } from "../../utils/utils";
import { StzhLocaleComponent } from "../../index";

import { StzhInputDescription } from "../stzh-input/stzh-input-description";

let radioCounter = 0;

/**
 * @slot - Slot for label content
 * @slot description - Slot for description
 * @slot description-long - Slot for long description (in popover)
 * @slot description-long-title - Slot for long description title (in popover) / instead of descriptionLongTitle or description property
 * @slot error - Slot for error
 */
@Component({
  tag: "stzh-radio",
  styleUrl: "stzh-radio.scss",
  scoped: true,
})
export class StzhRadio {
  /** Translation strings */
  @Prop() localization: StzhLocaleComponent;

  /** Whether the element is disabled or not */
  @Prop({ reflect: true }) disabled: boolean = false;

  /** The name of the input element */
  @Prop({ reflect: true }) name: string = "";

  /** The value of the input element */
  @Prop({ mutable: true }) value: string = "";

  /** Invalid status */
  @Prop({ reflect: true }) invalid: boolean = false;

  /** Required status */
  @Prop({ reflect: true }) required: boolean = false;

  /** Hide `(optional)` label (or use `required` inside form to hide it) */
  @Prop({ reflect: true }) hideOptional: boolean = false;

  /** Checked status */
  @Prop({ mutable: true, reflect: true }) checked: boolean = false;

  /** Default checked (used by reset) */
  @Prop({ mutable: true }) defaultChecked: boolean;

  /** Label */
  @Prop() label: string = "";

  /** Description message (use description slot as alternative) */
  @Prop() description: string;

  /** Long description message appearing in a popover (use description-long slot as alternative) */
  @Prop() descriptionLong: string;

  /**
  * **Do not use this property. It will be deprecated with the next major release.**
  * Long description title appearing in a popover (use description-long-title slot as or description property alternative) */
  @Prop() descriptionLongTitle: string;

  /** One or multiple error message (use error slot as alternative) */
  @Prop() error: string | string[];
  private _error: string[];

  /** Id for element which describes the radio button (this will be overwritten if description prop or slot is used, used by stzh-radiogroup) */
  @Prop({ attribute: "a11y-describedby" }) a11yDescribedby: string = "";

  /** Input change event */
  @Event() stzhChange: EventEmitter<StzhRadioChangeEvent>;

  /** Input focus event */
  @Event() stzhFocus: EventEmitter<StzhRadioFocusEvent>;

  /** Input blur event */
  @Event() stzhBlur: EventEmitter<StzhRadioBlurEvent>;

  @Listen("reset", { target: "document" })
  resetListener(event: Event) {
    if ((event.target as HTMLElement).contains(this.element)) {
      requestAnimationFrame(() => {
        this.handleReset();
      });
    }
  }

  @Watch("error")
  errorWatcher(newValue: string | string[]) {
    if (typeof newValue === "string") {
      try {
        this._error = JSON.parse(newValue);
      } catch (e) {
        if (newValue) {
          this._error = [newValue];
        } else {
          this._error = [];
        }
      }
    } else if (newValue) {
      this._error = newValue;
    } else {
      this._error = [];
    }
  }

  @Element() element: HTMLStzhRadioElement;

  private input: HTMLInputElement;
  private inputId: string;
  private focusedByInput: boolean = false;

  private handleReset = async () => {
    this.checked = this.defaultChecked;
  };

  private onInput = (event: InputEvent) => {
    this.checked = this.input.checked;
    this.stzhChange.emit({
      component: "stzh-radio",
      originalEvent: event,
      value: this.value,
      checked: this.checked,
    });
  };

  private onRootFocus = () => {
    if (!this.focusedByInput) {
      this.input.focus();
    }

    this.focusedByInput = false;
  };

  private onFocus = (event: FocusEvent) => {
    this.focusedByInput = true;

    const focusEvent = new FocusEvent("focus", {
      view: window,
      bubbles: false,
      cancelable: false,
    });

    this.element.dispatchEvent(focusEvent);
    this.stzhFocus.emit({
      component: "stzh-radio",
      originalEvent: event,
    });
  };

  private onBlur = (event: FocusEvent) => {
    const blurEvent = new FocusEvent("blur", {
      view: window,
      bubbles: false,
      cancelable: false,
    });

    this.element.dispatchEvent(blurEvent);
    this.stzhBlur.emit({
      component: "stzh-radio",
      originalEvent: event,
    });
  };

  async componentWillLoad() {
    this.inputId = `stzh-radio-${radioCounter++}`;
    this.errorWatcher(this.error);

    this.defaultChecked = typeof this.defaultChecked === "boolean" ? this.defaultChecked : this.checked;

    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "radio");
    }
  }

  render() {
    const descriptionUsed = hasSlot(this.element, "description") || !!this.description;
    const descriptionLongUsed = hasSlot(this.element, "description-long") || !!this.descriptionLong;
    const errorUsed = hasSlot(this.element, "error") || this._error?.length > 0;

    const classes = {
      "stzh-radio": true,
      "stzh-radio--has-description": descriptionUsed,
      "stzh-radio--has-description-long": descriptionLongUsed,
      "stzh-radio--has-error": errorUsed,
      "stzh-radio--is-required": this.required,
      "stzh-radio--is-invalid": this.invalid || errorUsed,
      "stzh-radio--is-disabled": this.disabled,
    };

    return (
      <Host is-invalid={this.invalid || errorUsed} tabindex={this.disabled ? null : "-1"} onFocus={this.onRootFocus}>
        <div class={classes}>
          <label class="stzh-radio__field-wrapper">
            <input
              class="stzh-radio__input"
              ref={el => (this.input = el as HTMLInputElement)}
              type="radio"
              id={this.inputId}
              name={this.name}
              value={this.value}
              disabled={this.disabled}
              aria-describedby={`${this.inputId}-description ${this.a11yDescribedby}`}
              aria-required={this.required ? "true" : "false"}
              aria-invalid={this.invalid ? "true" : "false"}
              checked={this.checked}
              defaultChecked={this.defaultChecked}
              onInput={this.onInput}
              onFocus={this.onFocus}
              onBlur={this.onBlur}
            />
            <div class="stzh-radio__mark">
              <div class="stzh-radio__check"></div>
            </div>
            <div class="stzh-radio__label">
              {this.label ? this.label : <slot></slot>}
              {!this.hideOptional && (
                <span class="stzh-radio__marker">
                  <span class="stzh-radio__marker-symbol" aria-hidden="true">
                    {this.required
                      ? this.localization.$globals.requiredFieldMarker
                      : this.localization.$globals.optionalFieldMarker}
                  </span>
                  <span class="stzh-radio__marker-text">
                    {this.required
                      ? this.localization.$globals.requiredFieldText
                      : this.localization.$globals.optionalFieldText}
                  </span>
                </span>
              )}
            </div>
          </label>
          {(this._error?.length > 0 || this.description) && (
            <StzhInputDescription
              classPrefix="stzh-radio"
              id={`${this.inputId}-description`}
              error={this._error}
              description={this.description}
              descriptionLong={this.descriptionLong}
              descriptionLongTitle={this.descriptionLongTitle}
              descriptionLongUsed={descriptionLongUsed}
              moreInfoButtonLabel={this.localization.$globals.moreInfoButtonLabel}
            />
          )}
        </div>
      </Host>
    );
  }
}
